import { onlyDigits } from 'shared/regex/regex';
import { StrictEmailValidation } from 'utils/form/validation';
import * as yup from 'yup';
import { cachedGetFirmsByFCA } from '../../api';
import { ADDRESS_NAMES } from '../Form/Address/types';
import addressValidation from '../Form/Address/validationSchema';
import { NO_CLUB_VALUE } from './components/FindClub/FindClub';
import { NO_NETWORK_VALUE } from './components/FindNetwork/FindNetwork';
import { FIELD_NAMES } from './types';

export enum CUSTOM_ERROR {
  NO_NETWORK = 'NO_NETWORK',
  NO_CLUB = 'NO_CLUB',
  FCA_DUPLICATED_FIRM = 'FCA_DUPLICATED_FIRM',
}

export const validationSchema = () =>
  yup.object().shape({
    [FIELD_NAMES.FIRM_NAME]: yup
      .string()
      .nullable()
      .required('This field is required')
      .typeError('Firm Name is invalid')
      .test('len', 'Firm Name should contain minimum 2 characters', (val) => val && val.length > 1),
    [FIELD_NAMES.FCA_NUMBER]: yup
      .string()
      .nullable()
      .required('This field is required.')
      .typeError('FCA Number is invalid')
      .matches(onlyDigits, 'FCA Number is invalid')
      .test('len', 'FCA Number should contain 6 or 7 characters', (val) => val && [6,7].includes(val.length))
      .test({
        name: CUSTOM_ERROR.FCA_DUPLICATED_FIRM,
        test: async function(value: string) {
          if (value && [6,7].includes(value.length)) {
            const foundFirms = await cachedGetFirmsByFCA(value);
            return foundFirms.length > 0
              ? this.createError({
                  path: FIELD_NAMES.FCA_NUMBER,
                  params: {
                    firm: foundFirms[0],
                  },
                })
              : true;
          }
          return true;
        },
      }),
    [FIELD_NAMES.FIRM_EMAIL]: StrictEmailValidation()
      .nullable()
      .required('This field is required.'),
    [FIELD_NAMES.FIRM_PHONE]: yup
      .string()
      .nullable()
      .required('This field is required')
      .matches(/[0]\d{4}(\s\d{3}\s\d{3}|\d{6})/, `Please enter a valid phone number.`)
      .test('len', `Please enter a valid phone number.`, (val) => {
        return val ? val.trim().length > 10 : true;
      }),
    [ADDRESS_NAMES.ADDRESS_LINE_1]: addressValidation.requiredAddressLine(),
    [ADDRESS_NAMES.ADDRESS_LINE_2]: addressValidation.optionalAddressLine(),
    [ADDRESS_NAMES.ADDRESS_LINE_3]: addressValidation.optionalAddressLine(),
    [ADDRESS_NAMES.CITY]: addressValidation.city(),
    [ADDRESS_NAMES.COUNTY]: addressValidation.county(),
    [ADDRESS_NAMES.POSTCODE]: addressValidation.postcode(),
    [FIELD_NAMES.HAS_PERMISSION_TO_REGISTER_FIRM]: yup
      .bool()
      .nullable()
      .oneOf(
        [true],
        'You must either obtain permission to register your firm with LiveMore, or ask someone who does have permission to register the firm',
      ),
    [FIELD_NAMES.NETWORK]: yup
      .string()
      .nullable()
      .when(FIELD_NAMES.FIRM_TYPE, {
        is: 'AR',
        then: yup
          .string()
          .nullable()
          .required('This field is required')
          .test({
            name: CUSTOM_ERROR.NO_NETWORK,
            test: (value: string) => value !== NO_NETWORK_VALUE,
          }),
      }),
    [FIELD_NAMES.FIRM_TYPE]: yup
      .string()
      .nullable()
      .oneOf(['DA', 'AR'], 'Please select an option'),
    [FIELD_NAMES.CLUB]: yup
      .string()
      .nullable()
      .when(FIELD_NAMES.FIRM_TYPE, {
        is: 'DA',
        then: yup
          .string()
          .nullable()
          .required('This field is required')
          .test({
            name: CUSTOM_ERROR.NO_CLUB,
            test: (value: string) => value !== NO_CLUB_VALUE,
          }),
      }),
  });
